import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useAuth from "./hooks/useAuth";

import useMainBackground from "./hooks/useMainBackground";
import SocketProvider from "./components/SocketProvider";
import RootModals from "./components/RootModals";
import useAllInsightEvents from "./hooks/useAllInsightEvents";
import useGlobalGameEvents from "./hooks/useGlobalGameEvents";
import useFirebaseWebNotification from "./hooks/useFirebaseWebNotification";
import useRNPurchaseProcess from "./hooks/useRNPurchaseProcess";
import "./styles/App.scss";
import useAllGameEvents from "./hooks/useAllGameEvents";
import useRefreshGameData from "./hooks/useRefreshGameData";
import PersonalAccount from "./store/PersonalAccount";
import BaseSpinner from "./components/BaseSpinner";
import { LOYALTY_REF_CODE_KEY } from "./utils/constants";
import Pricing from "./store/Pricing";
import { useTranslation } from "react-i18next";

ScrollToTop.propTypes = {
  children: PropTypes.node,
};
function ScrollToTop({ children }) {
  const { pathname } = useLocation();
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);

  return children;
}

function App() {
  const location = useLocation();
  const mainCls = useMainBackground();
  useGlobalGameEvents();
  useAllInsightEvents();
  useRefreshGameData();
  useAllGameEvents();
  useFirebaseWebNotification();
  useRNPurchaseProcess();
  const { checkAuth } = useAuth();
  const { t, i18n } = useTranslation();

  const [insetsTop, setInsetsTop] = useState(window?.insetsTop);
  const [insetsBottom, setInsetsBottom] = useState(window?.insetsBottom);
  const [isInitialized, setIsInitialized] = useState(false);

  // Этап 2: захват per-user реф-кода из ?ref= (шеринг-ссылка лояльности).
  // Код уйдёт полем refCode при регистрации (query ?ref= бизнес-партнёрки не трогаем).
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) {
        localStorage.setItem(LOYALTY_REF_CODE_KEY, ref);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Серверный справочник цен (публичный) — один раз на загрузку приложения,
  // чтобы кнопки с ценой (лендинг, ЛК, модалки) не дёргали fetch из render.
  useEffect(() => {
    Pricing.fetch();
  }, []);

  useEffect(() => {
    const title = t("app.title");
    document.title = title;
    // Keep og:title/twitter:title in sync so Safari's share-sheet
    // doesn't flicker between document.title (changes with i18n) and
    // a static OG tag (frozen at HTML build time, RU on .ru even if
    // user has EN locale selected via langSwitcher).
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute("content", title);
  }, [t, i18n.language]);

  useEffect(() => {
    (async () => {
      const isAuthenticated = await checkAuth();

      // ✅ Загружаем данные аккаунта только если пользователь авторизован
      if (isAuthenticated) {
        await PersonalAccount.getPersonalAccountData();
      }

      setIsInitialized(true);
    })();
  }, []);

  useEffect(() => {
    window.postMessage(
      JSON.stringify({
        type: "LOCATION",
        data: {
          location,
        },
      }),
      "*"
    );
  }, [location]);

  useEffect(() => {
    setInsetsTop(window?.insetsTop);
    setInsetsBottom(window?.insetsBottom);
  }, []);

  useEffect(() => {
    if (insetsTop) document.body.style.paddingTop = `${insetsTop}px`;
    if (insetsBottom) document.body.style.paddingBottom = `${insetsBottom}px`;
  }, [insetsTop, insetsBottom]);

  if (!isInitialized) {
    // Блокируем рендер страниц до завершения инициализации:
    // 1. checkAuth() - проверяет токен авторизации
    // 2. getPersonalAccountData() - загружает данные пользователя (только если авторизован)
    // Это гарантирует, что все страницы получат актуальные данные при монтировании
    return (
      <div className={mainCls}>
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <BaseSpinner type="light" />
        </div>
      </div>
    );
  }

  return (
    <div className={mainCls}>
      <div className="container">
        <RootModals />
        <Outlet />
      </div>
    </div>
  );
}

function WrappedApp() {
  return <App />;
}

export default function BrowserApp() {
  return (
    <SocketProvider>
      <ScrollToTop>
        <WrappedApp />
      </ScrollToTop>
    </SocketProvider>
  );
}
